/**
 * The listening bars follow the microphone's level (UX spec v3 §4: 4–24 px,
 * `AnalyserNode`, one frame per 80 ms). A second consumer of the microphone,
 * beside the speech engine; if the browser refuses it, the bars stay flat and
 * listening goes on — the level is decoration, never a gate.
 */
export function barLevels(bins: Uint8Array, bars: number): number[] {
  // Speech lives in the low bins; spread the first half of the spectrum over the bars.
  const span = Math.max(1, Math.floor(bins.length / 2 / bars));
  const out: number[] = [];
  for (let b = 0; b < bars; b += 1) {
    let sum = 0;
    for (let i = 0; i < span; i += 1) sum += bins[b * span + i] ?? 0;
    out.push(Math.min(1, sum / span / 200));
  }
  return out;
}

export class LevelMeter {
  #stream: MediaStream | undefined;
  #ctx: AudioContext | undefined;
  #timer: ReturnType<typeof setInterval> | undefined;
  /** Bumped by every start and stop: a permission prompt can outlive the listening it was for. */
  #generation = 0;

  /**
   * Called INSIDE the tap. The audio context is made and resumed before the
   * first await: Safari (and Chrome's autoplay rule) only lets a context run when
   * it was started in a user gesture, and a gesture does not survive an await —
   * a context made after `getUserMedia` resolves stays suspended and meters zeros.
   */
  async start(bars: number, onLevels: (levels: number[]) => void): Promise<boolean> {
    this.stop();
    const generation = this.#generation;
    const media = globalThis.navigator?.mediaDevices;
    const Ctx = (globalThis as unknown as { AudioContext?: typeof AudioContext }).AudioContext;
    if (media?.getUserMedia === undefined || Ctx === undefined) return false;
    let ctx: AudioContext;
    try {
      ctx = new Ctx();
      void ctx.resume().catch(() => undefined);
    } catch {
      return false;
    }
    this.#ctx = ctx;
    try {
      const stream = await media.getUserMedia({ audio: true });
      if (generation !== this.#generation) {
        // Listening ended while the browser was still asking: close the microphone, meter nothing.
        stream.getTracks().forEach((t) => t.stop());
        return false;
      }
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 128;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const bins = new Uint8Array(analyser.frequencyBinCount);
      this.#stream = stream;
      this.#timer = setInterval(() => {
        analyser.getByteFrequencyData(bins);
        onLevels(barLevels(bins, bars));
      }, 80);
      return true;
    } catch {
      return false;
    }
  }

  stop(): void {
    this.#generation += 1;
    if (this.#timer !== undefined) clearInterval(this.#timer);
    this.#timer = undefined;
    this.#stream?.getTracks().forEach((t) => t.stop());
    this.#stream = undefined;
    void this.#ctx?.close().catch(() => undefined);
    this.#ctx = undefined;
  }
}

/**
 * DCTA-89 (Umakhan's iPhone, rec-e-2321: the first dictation landed, every later one listened to
 * FLAT bars and heard nothing — the microphone was dead to both the speech engine and this meter).
 * On iOS the speech engine is the microphone's only user: the meter never opens a second capture
 * (no getUserMedia, no AudioContext) and the bars follow the engine's words instead (SpeechPulse).
 * Every browser on iOS is WebKit; an iPad asks for the desktop site as "MacIntel" with touch.
 */
export function micShareable(nav: { readonly userAgent: string; readonly platform: string; readonly maxTouchPoints: number }): boolean {
  const ios = /\b(iPhone|iPad|iPod)\b/.test(nav.userAgent) || (nav.platform === "MacIntel" && nav.maxTouchPoints > 1);
  return !ios;
}

/**
 * DCTA-181: a chunk's RMS (0..1 of full scale) as the bars' loudness: -50 dBFS (a quiet room) is 0, -10 dBFS (a loud
 * voice close to the phone) is 1, linear in decibels between — speech at -30 dBFS fills half.
 */
export function loudness(rms: number): number {
  if (!Number.isFinite(rms) || rms <= 0) return 0;
  const db = 20 * Math.log10(rms);
  return Math.min(1, Math.max(0, (db + 50) / 40));
}

/**
 * Bars that rise with each word the engine hears — or (DCTA-181) with each chunk the microphone gives, when the engine
 * holds it (Tencent's) — and settle within a second. No microphone of their own.
 */
export class SpeechPulse {
  #timer: ReturnType<typeof setInterval> | undefined;
  #energy = 0;
  #tick = 0;

  start(bars: number, onLevels: (levels: number[]) => void): void {
    this.stop();
    this.#energy = 0;
    this.#tick = 0;
    this.#timer = setInterval(() => {
      this.#tick += 1;
      const e = this.#energy;
      const out: number[] = [];
      for (let b = 0; b < bars; b += 1) {
        // A fixed, uneven shape that drifts a little each frame, scaled by the energy left.
        const shape = 0.55 + 0.45 * Math.abs(Math.sin(b * 1.7 + this.#tick * 0.9));
        out.push(e === 0 ? 0 : Math.min(1, e * shape));
      }
      onLevels(out);
      this.#energy = e < 0.05 ? 0 : e * 0.72;
    }, 80);
  }

  /** A word arrived. */
  kick(): void {
    this.#energy = 1;
  }

  /** DCTA-181: a chunk of the microphone, its RMS — the bars follow the real voice from the first chunk. */
  level(rms: number): void {
    this.#energy = Math.max(this.#energy, loudness(rms));
  }

  stop(): void {
    if (this.#timer !== undefined) clearInterval(this.#timer);
    this.#timer = undefined;
    this.#energy = 0;
  }
}
